import React, { useRef, useState, useEffect } from 'react'
import { View, TouchableOpacity, Text, StyleSheet, Platform } from 'react-native'
import { Audio } from 'expo-av'
import { Feather } from '@expo/vector-icons'
import theme from '../../../../theme'
// import SkiaAudioWave from './SkiaAudioWave'
export default function AudioPlayerWaveform({ url, title, post_id, onEnd, nearEndThresholdSec = 0.3 }) {
    // ...existing code...
    // (colocar la función justo antes del return)
    // ...existing code...
    const formatTime = (sec) => {
        if (!sec || isNaN(sec)) return '0:00'
        const m = Math.floor(sec / 60)
        const s = Math.floor(sec % 60)
        return `${m}:${s.toString().padStart(2, '0')}`
    }
    const [isPlaying, setIsPlaying] = useState(false)
    const [sound, setSound] = useState(null)
    const [localAudioUri, setLocalAudioUri] = useState(null)
    const [audioStatus, setAudioStatus] = useState({ positionMillis: 0, durationMillis: 1 })
    const canvasRef = useRef(null)
    const endFiredRef = useRef(false)

    const [progress, setProgress] = useState(0)
    // useEffect(() => {
    //     let interval

    //     if (isPlaying) {
    //         interval = setInterval(() => {
    //             setProgress((p) => p + 0.05)
    //         }, 16) // ~60 FPS
    //     }

    //     return () => clearInterval(interval)
    // }, [isPlaying])

    // Resetear end flag cuando cambia la url
    useEffect(() => {
        endFiredRef.current = false
    }, [url])

    // Descargar el audio localmente en mobile
    useEffect(() => {
        if (Platform.OS !== 'web' && url) {
            ;(async () => {
                try {
                    const { uri } = await Audio.Sound.createAsync({ uri: url }, {}, undefined, true)
                    console.log('[AudioPlayerWaveform][mobile] Archivo local descargado:', uri)
                    setLocalAudioUri(uri)
                } catch (e) {
                    console.log('[AudioPlayerWaveform][mobile] Error al descargar audio:', e)
                    setLocalAudioUri(null)
                }
            })()
        }
    }, [url])

    // Play/pause para ambos entornos
    const maybeFireEnd = () => {
        try {
            if (endFiredRef.current) return
            if (typeof onEnd === 'function') {
                endFiredRef.current = true
                onEnd()
            }
        } catch {}
    }

    const playPause = async () => {
        if (!sound) {
            setIsPlaying(true)
            const { sound: newSound } = await Audio.Sound.createAsync({ uri: localAudioUri || url }, { shouldPlay: true })
            setSound(newSound)
            newSound.setOnPlaybackStatusUpdate(async (status) => {
                setAudioStatus(status)
                // Fin natural: disparar onEnd una sola vez
                if (status.didJustFinish) {
                    setIsPlaying(false)
                    maybeFireEnd()
                    // Reiniciar posición al final (opcional)
                    try {
                        await newSound.setPositionAsync(0)
                    } catch {}
                    return
                }
                // Cerca del final: si se pausa o se detiene, considerar como fin
                try {
                    const dur = status.durationMillis || 0
                    const pos = status.positionMillis || 0
                    const timeLeft = (dur - pos) / 1000
                    if (dur > 0 && timeLeft <= nearEndThresholdSec && pos > 2000) {
                        // Si no está reproduciendo (pausa manual cerca del final) o quedó detenido
                        if (!status.isPlaying) {
                            maybeFireEnd()
                        }
                    }
                } catch {}
            })
        } else {
            if (isPlaying) {
                await sound.pauseAsync()
                setIsPlaying(false)
            } else {
                // Si terminó, reiniciar posición antes de reproducir
                const status = await sound.getStatusAsync()
                if (status.positionMillis >= (status.durationMillis || 1) - 500) {
                    await sound.setPositionAsync(0)
                }
                await sound.playAsync()
                setIsPlaying(true)
            }
        }
    }

    // Visualización de ondas en web usando WebAudio API
    // Animación de ondas en web usando AnalyserNode
    // Estado para controles web
    const [webAudioState, setWebAudioState] = useState({
        duration: 0,
        currentTime: 0,
        buffered: 0,
        paused: true,
    })

    // Ref para el elemento de audio en web
    const webAudioRef = useRef(null)
    useEffect(() => {
        if (Platform.OS === 'web' && url && canvasRef.current) {
            let audioCtx, source, analyser, animationId
            const canvas = canvasRef.current
            const ctx = canvas.getContext('2d')
            ctx.clearRect(0, 0, canvas.width, canvas.height)

            // Crear elemento de audio oculto y guardar en ref
            let audioElement = document.createElement('audio')
            audioElement.src = url
            audioElement.crossOrigin = 'anonymous'
            audioElement.preload = 'auto'
            audioElement.style.display = 'none'
            document.body.appendChild(audioElement)
            webAudioRef.current = audioElement
            console.log('[AudioPlayerWaveform] Elemento <audio> creado:', audioElement)

            audioCtx = new (window.AudioContext || window.webkitAudioContext)()
            source = audioCtx.createMediaElementSource(audioElement)
            analyser = audioCtx.createAnalyser()
            analyser.fftSize = 256
            source.connect(analyser)
            analyser.connect(audioCtx.destination)

            const bufferLength = analyser.frequencyBinCount
            const dataArray = new Uint8Array(bufferLength)

            function draw() {
                animationId = requestAnimationFrame(draw)
                analyser.getByteTimeDomainData(dataArray)
                ctx.clearRect(0, 0, canvas.width, canvas.height)
                ctx.beginPath()
                const sliceWidth = (canvas.width * 1.0) / bufferLength
                let x = 0
                for (let i = 0; i < bufferLength; i++) {
                    const v = dataArray[i] / 128.0
                    const y = (v * canvas.height) / 2
                    if (i === 0) {
                        ctx.moveTo(x, y)
                    } else {
                        ctx.lineTo(x, y)
                    }
                    x += sliceWidth
                }
                ctx.strokeStyle = theme.colors.colorPrimary
                ctx.lineWidth = 2

                ctx.stroke()
            }

            draw()

            // Actualizar estado de controles
            const updateState = () => {
                setWebAudioState({
                    duration: audioElement.duration || 0,
                    currentTime: audioElement.currentTime || 0,
                    buffered: audioElement.buffered.length > 0 ? audioElement.buffered.end(audioElement.buffered.length - 1) : 0,
                    paused: audioElement.paused,
                })
                // Near-end detection en web
                try {
                    const dur = audioElement.duration || 0
                    const cur = audioElement.currentTime || 0
                    const timeLeft = dur > 0 ? dur - cur : 0
                    if (dur > 0 && timeLeft <= nearEndThresholdSec && cur > 2) {
                        if (audioElement.paused) {
                            maybeFireEnd()
                        }
                    }
                } catch {}
            }
            // Fin natural
            audioElement.addEventListener('ended', () => {
                maybeFireEnd()
            })
            audioElement.addEventListener('timeupdate', updateState)
            audioElement.addEventListener('progress', updateState)
            audioElement.addEventListener('play', updateState)
            audioElement.addEventListener('pause', updateState)
            audioElement.addEventListener('loadedmetadata', updateState)

            // Play/pause sincronizado con el botón
            const playPauseWeb = async () => {
                if (webAudioRef.current) {
                    try {
                        // Forzar reanudación del contexto de audio
                        if (audioCtx.state === 'suspended') {
                            await audioCtx.resume()
                            console.log('[AudioPlayerWaveform] AudioContext reanudado')
                        }
                        if (webAudioRef.current.paused) {
                            await webAudioRef.current.play()
                            console.log('[AudioPlayerWaveform] Play solicitado')
                        } else {
                            webAudioRef.current.pause()
                            console.log('[AudioPlayerWaveform] Pause solicitado')
                        }
                    } catch (err) {
                        console.error('[AudioPlayerWaveform] Error al reproducir audio:', err)
                        alert('No se pudo reproducir el audio. Verifica el formato o permisos.')
                    }
                } else {
                    console.warn('[AudioPlayerWaveform] No hay elemento <audio> para play/pause')
                }
            }
            // Avanzar/retroceder 10s
            const seekWeb = (delta) => {
                if (webAudioRef.current) {
                    webAudioRef.current.currentTime = Math.max(
                        0,
                        Math.min(webAudioRef.current.currentTime + delta, webAudioRef.current.duration || 0),
                    )
                    updateState()
                    console.log('[AudioPlayerWaveform] Seek solicitado:', delta)
                }
            }
            // Reemplazar playPause y seek solo en web
            if (Platform.OS === 'web') {
                window.playPauseWebAudio = playPauseWeb
                window.seekWebAudio = seekWeb
            }

            return () => {
                cancelAnimationFrame(animationId)
                if (webAudioRef.current) {
                    webAudioRef.current.pause()
                    webAudioRef.current.remove()
                    webAudioRef.current = null
                }
                audioCtx.close()
            }
        }
    }, [url, canvasRef.current])

    // Transcripción eliminada
    useEffect(() => {
        return () => {
            // Detener audio en mobile
            if (sound) {
                sound.stopAsync?.()
                sound.unloadAsync?.()
            }
        }
    }, [sound])
    const opacityBackground = theme.colors.colorPrimary + `26` //? 15% opacity
    return (
        <View style={[styles.container, { backgroundColor: opacityBackground }]}>
            <Text style={styles.title}>{title || 'PODCAST'}</Text>
            {Platform.OS === 'web' ? (
                <View style={[styles.waveformBar, { flexDirection: 'column', marginBottom: 0, padding: 0 }]}>
                    {/* Ondas arriba */}
                    <canvas ref={canvasRef} width={360} height={60} style={{ borderRadius: 8, marginBottom: 12 }} />
                    {/* Controles en una línea debajo */}
                    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginBottom: 0 }}>
                        <TouchableOpacity onPress={() => window.seekWebAudio && window.seekWebAudio(-10)} style={{ marginRight: 8 }}>
                            <Feather name={'rotate-ccw'} size={24} color={theme.colors.colorPrimary} />
                        </TouchableOpacity>
                        <TouchableOpacity onPress={() => window.playPauseWebAudio && window.playPauseWebAudio()} style={{ marginRight: 8 }}>
                            <Feather name={webAudioState.paused ? 'play' : 'pause'} size={32} color={theme.colors.colorPrimary} />
                        </TouchableOpacity>
                        <TouchableOpacity onPress={() => window.seekWebAudio && window.seekWebAudio(10)}>
                            <Feather name={'rotate-cw'} size={24} color={theme.colors.colorPrimary} />
                        </TouchableOpacity>
                    </View>
                    {/* Barra de progreso y tiempos centrados */}
                    <View style={{ width: 300, alignSelf: 'center', marginTop: 10 }}>
                        <View style={{ height: 6, backgroundColor: '#e0e7ef', borderRadius: 3, position: 'relative' }}>
                            <View
                                style={{
                                    position: 'absolute',
                                    left: 0,
                                    top: 0,
                                    height: 6,
                                    width: `${webAudioState.duration ? (webAudioState.currentTime / webAudioState.duration) * 100 : 0}%`,
                                    backgroundColor: theme.colors.colorPrimary,
                                    borderRadius: 3,
                                }}
                            />
                        </View>
                        <View style={{ alignItems: 'center', marginTop: 4 }}>
                            <Text style={{ fontSize: 12, color: '#222' }}>
                                {`${formatTime(webAudioState.currentTime)} / ${formatTime(webAudioState.duration)}`}
                            </Text>
                        </View>
                    </View>
                </View>
            ) : (
                <View style={[styles.waveformBar, { flexDirection: 'column', marginBottom: 0, padding: 0 }]}>
                    {/* Ondas arriba */}
                    {/* <SkiaAudioWave progress={progress} /> */}

                    {/* Controles en una línea debajo */}
                    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
                        <TouchableOpacity
                            onPress={async () => {
                                if (sound) {
                                    const status = await sound.getStatusAsync()
                                    await sound.setPositionAsync(Math.max(0, status.positionMillis - 10000))
                                }
                            }}
                            style={{ marginRight: 8 }}
                            disabled={isPlaying && sound === null} // Evita doble tap mientras inicializa
                        >
                            <Feather name={'rotate-ccw'} size={24} color={theme.colors.colorPrimary} />
                        </TouchableOpacity>
                        <TouchableOpacity
                            onPress={async () => {
                                if (isPlaying && sound === null) return // Evita doble tap mientras inicializa
                                await playPause()
                            }}
                            style={{ marginRight: 8 }}
                            disabled={isPlaying && sound === null} // Evita doble tap mientras inicializa
                        >
                            <Feather name={isPlaying ? 'pause' : 'play'} size={32} color={theme.colors.colorPrimary} />
                        </TouchableOpacity>
                        <TouchableOpacity
                            onPress={async () => {
                                if (sound) {
                                    const status = await sound.getStatusAsync()
                                    await sound.setPositionAsync(Math.min(status.durationMillis || 0, status.positionMillis + 10000))
                                }
                            }}
                        >
                            <Feather name={'rotate-cw'} size={24} color={theme.colors.colorPrimary} />
                        </TouchableOpacity>
                    </View>
                    {/* Barra de progreso y tiempos centrados */}
                    {sound && (
                        <View style={{ width: 300, alignSelf: 'center', marginTop: 8 }}>
                            <View style={{ height: 6, backgroundColor: '#e0e7ef', borderRadius: 3, position: 'relative' }}>
                                {/* Progreso */}
                                <View
                                    style={{
                                        position: 'absolute',
                                        left: 0,
                                        top: 0,
                                        height: 6,
                                        width: `${(audioStatus.positionMillis / (audioStatus.durationMillis || 1)) * 100}%`,
                                        backgroundColor: '#3b82f6',
                                        borderRadius: 3,
                                    }}
                                />
                            </View>
                            <View style={{ alignItems: 'center', marginTop: 4 }}>
                                <Text style={{ fontSize: 12, color: '#222' }}>
                                    {`${formatTime((audioStatus.positionMillis || 0) / 1000)} / ${formatTime(
                                        (audioStatus.durationMillis || 0) / 1000,
                                    )}`}
                                </Text>
                            </View>
                        </View>
                    )}
                </View>
            )}
        </View>
    )
}

// Utilidad para formatear tiempo mm:ss (si se necesita fuera del componente)
// const formatTime = (sec) => {
//   if (!sec || isNaN(sec)) return '0:00';
//   const m = Math.floor(sec / 60);
//   const s = Math.floor(sec % 60);
//   return `${m}:${s.toString().padStart(2, '0')}`;
// };

const styles = StyleSheet.create({
    container: {
        borderRadius: 12,
        padding: 16,
        alignItems: 'center',
        marginVertical: 12,
        // shadowColor: '#000',
        // shadowOpacity: 0.08,
        // shadowRadius: 8,
        // elevation: 2,
    },
    title: {
        fontWeight: 'bold',
        fontSize: 16,
        marginBottom: 8,
        color: theme.colors.colorPrimary,
    },
    waveformBar: {
        flexDirection: 'row',
        alignItems: 'center',
        borderRadius: 8,
        padding: 8,
        marginBottom: 12,
        width: '100%',
        justifyContent: 'center',
    },
    transcribeBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#e0e7ef',
        borderRadius: 8,
        padding: 8,
        marginBottom: 8,
    },
    transcriptBox: {
        backgroundColor: '#fff',
        borderRadius: 8,
        padding: 10,
        marginTop: 8,
        width: '100%',
    },
})
